import type { Message } from '@supporchat/shared';
import { api } from '../lib/api';
import { useVoiceRecorder } from '../hooks/useVoiceRecorder';

/**
 * Grava e envia mensagem de voz para o cliente.
 *
 * O atendimento recebia áudio e não podia responder em áudio — o que obriga o
 * atendente a digitar a explicação que ele daria falando em quinze segundos,
 * justamente nos casos técnicos ("gira o parafuso meio giro pra dentro").
 *
 * Enquanto grava, o botão de enviar texto continua ali: nada é bloqueado, e o
 * tempo aparece para o atendente saber quanto já falou.
 */
export function VoiceRecorderButton({
  conversationId,
  onSent,
  onError,
}: {
  conversationId: string;
  onSent: (message: Message) => void;
  onError: (message: string) => void;
}) {
  const recorder = useVoiceRecorder();

  if (!recorder.supported) return null;

  const send = async () => {
    const blob = await recorder.stop();

    if (!blob) {
      recorder.setSending(false);
      onError('Nada foi gravado.');
      return;
    }

    recorder.setSending(true);
    try {
      onSent(await api.sendAudioMessage(conversationId, blob));
    } catch (err) {
      onError((err as Error).message);
    } finally {
      recorder.setSending(false);
    }
  };

  if (recorder.phase === 'recording') {
    return (
      <span className="recorder">
        <span className="recorder__dot" aria-hidden="true" />
        <span className="recorder__timer">{formatSeconds(recorder.seconds)}</span>
        <button className="btn btn--sm btn--primary" onClick={() => void send()}>
          Enviar áudio
        </button>
        <button className="btn btn--sm" onClick={recorder.cancel}>
          Descartar
        </button>
      </span>
    );
  }

  return (
    <button
      className="btn"
      title={recorder.error ?? 'Gravar mensagem de voz'}
      disabled={recorder.phase === 'sending'}
      onClick={() => {
        recorder.clearError();
        void recorder.start();
      }}
    >
      {recorder.phase === 'sending' ? 'Enviando...' : '🎤'}
    </button>
  );
}

function formatSeconds(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${String(minutes).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
}
